/**
 * MediaService handles all audio and video functionality for the chat system
 *
 * This service manages media streams, device enumeration, permissions, and media controls
 * for both local (this user's) and remote (the other participant's) streams. It provides
 * cross-browser compatibility including special handling for Safari, and supports degraded
 * mode operation when media APIs are unavailable.
 *
 * Note: Video resolution constraints removed to improve mobile compatibility.
 * Browser will select optimal resolution based on device capabilities.
 *
 * @class MediaService
 */
class MediaService {
  /**
   * Creates a new MediaService instance
   *
   * Initializes all internal state for managing media streams, devices, permissions,
   * and event listeners. The service tracks both local and remote streams and
   * maintains state for audio/video controls.
   */
  constructor() {
    /** @type {MediaStream|null} Local media stream (this user's video/audio) */
    this.localStream = null

    /** @type {MediaStream|null} Remote media stream (the other participant's video/audio) */
    this.remoteStream = null

    /** @type {Object|null} EasyRTC instance for enableMicrophone calls */
    this.easyrtc = null

    /**
     * @type {Object} Available media devices
     * @property {Array<MediaDeviceInfo>} audioInputs - Available audio input devices
     * @property {Array<MediaDeviceInfo>} videoInputs - Available video input devices
     * @property {Array<MediaDeviceInfo>} audioOutputs - Available audio output devices
     */
    this.mediaDevices = {
      audioInputs: [],
      videoInputs: [],
      audioOutputs: [],
    }

    /**
     * @type {Object} Currently selected devices
     * @property {string|null} audioInput - Device ID of selected audio input
     * @property {string|null} videoInput - Device ID of selected video input
     * @property {string|null} audioOutput - Device ID of selected audio output
     */
    this.currentDevices = {
      audioInput: null,
      videoInput: null,
      audioOutput: null,
    }

    /**
     * @type {Object} Current media state
     * @property {boolean} audioEnabled - Whether audio is enabled
     * @property {boolean} videoEnabled - Whether video is enabled
     * @property {boolean} audioMuted - Whether audio is muted
     * @property {boolean} videoPaused - Whether video is paused
     * @property {Object} permissions - Permission states ('granted', 'denied', 'unknown')
     */
    this.mediaState = {
      audioEnabled: false,
      videoEnabled: false,
      audioMuted: false,
      videoPaused: false,
      permissions: {
        audio: 'unknown',
        video: 'unknown',
      },
    }

    /** @type {Map<string, Set<Function>>} Map of event names to their callback sets */
    this.listeners = new Map()

    /**
     * @type {Object} Video element references
     * @property {HTMLVideoElement|null} selfVideo - Reference to local video element
     * @property {HTMLVideoElement|null} callerVideo - Reference to remote video element
     */
    this.videoRefs = {
      selfVideo: null,
      callerVideo: null,
    }

    /**
     * @type {Object} Media constraints for getUserMedia
     * @property {Object} audio - Audio constraints with echo cancellation, noise suppression, etc.
     * @property {boolean|Object} video - Video constraints (true = browser chooses optimal resolution)
     *
     * Default lists video:true for video/both calls only. Audio-only CallService /
     * CoachChatManager paths use easyrtc.enableVideo(false) so getUserMedia does
     * not acquire a camera track (CTS-9778 / CC-UX-013).
     */
    this.constraints = {
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
      video: true, // Used only when mediaType is 'video' or 'both'
    }
  }

  /**
   * Sets the EasyRTC instance for enableMicrophone calls
   *
   * The EasyRTC instance is required for muting/unmuting audio using EasyRTC's
   * enableMicrophone method, which maintains compatibility with the legacy system.
   *
   * @param {Object} easyrtc - The EasyRTC instance
   */
  setEasyRTC(easyrtc) {
    this.easyrtc = easyrtc
  }

  /**
   * Initializes the media service and checks permissions
   *
   * Performs initial setup including checking browser support, verifying secure
   * context (required for Safari), checking permissions, enumerating devices, and
   * setting up event listeners. Returns true even in degraded mode to allow the
   * service to continue operating without media functionality.
   *
   * @returns {Promise<boolean>} True if initialization succeeded (including degraded mode), false on failure
   */
  async initialize() {
    try {
      // Check if media devices are supported
      // Safari supports these APIs but may need special handling
      const isSafari = /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent,
      )
      const hasMediaDevices =
        navigator.mediaDevices && navigator.mediaDevices.getUserMedia

      if (!hasMediaDevices && !isSafari) {
        console.warn(
          'Media devices not supported in this browser, MediaService will run in degraded mode',
        )
        this.notifyListeners('initialized', this.mediaState)
        return true // Return true to allow the service to work in degraded mode
      }

      // For Safari, check if we're in a secure context (required for media access)
      if (isSafari && !window.isSecureContext) {
        console.warn(
          'Safari requires HTTPS for media device access. MediaService will run in degraded mode.',
        )
        this.notifyListeners('initialized', this.mediaState)
        return true // Return true to allow the service to work in degraded mode
      }

      // Check permissions status
      await this.checkPermissions()

      // Enumerate available devices
      await this.enumerateDevices()

      // Set up media stream event listeners
      this._setupMediaEventListeners()

      this.notifyListeners('initialized', this.mediaState)
      return true
    } catch (error) {
      console.error('MediaService initialization failed:', error)
      this.notifyListeners('initializationFailed', { error: error.message })
      return false
    }
  }

  /**
   * Checks current permission status for audio and video
   *
   * Queries the browser's Permissions API to determine the current state of
   * microphone and camera permissions. Safari doesn't support the Permissions API
   * for media devices, so permissions are set to 'unknown' and will be determined
   * when getUserMedia is called.
   *
   * @returns {Promise<void>}
   */
  async checkPermissions() {
    try {
      const isSafari = /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent,
      )

      // Safari doesn't support the Permissions API for media devices
      if (isSafari) {
        // For Safari, we can't query permissions without requesting them first
        // Set to unknown and let the actual getUserMedia call determine the result
        this.mediaState.permissions.audio = 'unknown'
        this.mediaState.permissions.video = 'unknown'
        this.notifyListeners('permissionsUpdated', this.mediaState.permissions)
        return
      }

      // Check audio permission
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const audioPermission = await navigator.permissions.query({
            name: 'microphone',
          })
          this.mediaState.permissions.audio = audioPermission.state
        } catch (permError) {
          console.warn('Could not query microphone permission:', permError)
          this.mediaState.permissions.audio = 'unknown'
        }

        try {
          const videoPermission = await navigator.permissions.query({
            name: 'camera',
          })
          this.mediaState.permissions.video = videoPermission.state
        } catch (permError) {
          console.warn('Could not query camera permission:', permError)
          this.mediaState.permissions.video = 'unknown'
        }
      } else {
        this.mediaState.permissions.audio = 'unknown'
        this.mediaState.permissions.video = 'unknown'
      }

      this.notifyListeners('permissionsUpdated', this.mediaState.permissions)
    } catch (error) {
      console.warn('Could not check permissions:', error)
      // Set default permission states
      this.mediaState.permissions.audio = 'unknown'
      this.mediaState.permissions.video = 'unknown'
    }
  }

  /**
   * Enumerates available media devices
   *
   * Discovers all available audio input, video input, and audio output devices
   * on the system. Sets the first available device as the default for each type.
   * Requires secure context (HTTPS) for Safari. Does not throw errors as device
   * enumeration is not critical for basic functionality.
   *
   * @returns {Promise<void>}
   */
  async enumerateDevices() {
    try {
      // Check if enumerateDevices is supported
      const isSafari = /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent,
      )
      const hasMediaDevices =
        navigator.mediaDevices && navigator.mediaDevices.enumerateDevices

      if (!hasMediaDevices && !isSafari) {
        console.warn('enumerateDevices not supported in this browser')
        return
      }

      // For Safari, check secure context
      if (isSafari && !window.isSecureContext) {
        console.warn('Safari requires HTTPS for device enumeration')
        return
      }

      const devices = await navigator.mediaDevices.enumerateDevices()

      this.mediaDevices.audioInputs = devices.filter(
        (device) => device.kind === 'audioinput',
      )
      this.mediaDevices.videoInputs = devices.filter(
        (device) => device.kind === 'videoinput',
      )
      this.mediaDevices.audioOutputs = devices.filter(
        (device) => device.kind === 'audiooutput',
      )

      // Set default devices
      if (this.mediaDevices.audioInputs.length > 0) {
        this.currentDevices.audioInput =
          this.mediaDevices.audioInputs[0].deviceId
      }
      if (this.mediaDevices.videoInputs.length > 0) {
        this.currentDevices.videoInput =
          this.mediaDevices.videoInputs[0].deviceId
      }

      this.notifyListeners('devicesUpdated', this.mediaDevices)
    } catch (error) {
      console.error('Failed to enumerate devices:', error)
      // Don't throw - this is not critical for basic functionality
    }
  }

  /**
   * Requests media permissions and initializes streams
   *
   * Requests access to the user's microphone and/or camera based on the mediaType.
   * Applies device constraints if specific devices are selected. Handles various
   * error types (permission denied, device not found, device in use, security errors)
   * and provides Safari-specific error messages when appropriate.
   *
   * @param {string} [mediaType='both'] - Type of media to request: 'audio', 'video', or 'both'
   * @returns {Promise<MediaStream>} The obtained media stream
   * @throws {Error} If media devices are not supported, secure context required, or permission denied
   */
  async requestMediaAccess(mediaType = 'both') {
    try {
      // Check if media devices are supported
      const isSafari = /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent,
      )
      const hasMediaDevices =
        navigator.mediaDevices && navigator.mediaDevices.getUserMedia

      if (!hasMediaDevices && !isSafari) {
        throw new Error('Media devices not supported in this browser')
      }

      // For Safari, check secure context
      if (isSafari && !window.isSecureContext) {
        throw new Error('Safari requires HTTPS for media device access')
      }

      let constraints = {}

      if (mediaType === 'audio' || mediaType === 'both') {
        constraints.audio = {
          ...this.constraints.audio,
          deviceId: this.currentDevices.audioInput
            ? { exact: this.currentDevices.audioInput }
            : undefined,
        }
      }

      if (mediaType === 'video' || mediaType === 'both') {
        // Only specify deviceId if one is selected, otherwise let browser choose
        constraints.video = this.currentDevices.videoInput
          ? { deviceId: { exact: this.currentDevices.videoInput } }
          : true
      }

      const stream = await navigator.mediaDevices.getUserMedia(constraints)

      // Update media state
      this.mediaState.audioEnabled = stream.getAudioTracks().length > 0
      this.mediaState.videoEnabled = stream.getVideoTracks().length > 0

      // Store the local stream
      this.localStream = stream

      // Update permissions
      await this.checkPermissions()

      this.notifyListeners('mediaAccessGranted', {
        stream,
        mediaState: this.mediaState,
      })

      return stream
    } catch (error) {
      console.error('Media access request failed:', error)

      // Handle specific permission errors
      if (error.name === 'NotAllowedError') {
        this.mediaState.permissions.audio = 'denied'
        this.mediaState.permissions.video = 'denied'

        // Provide Safari-specific guidance
        const isSafari = /^((?!chrome|android).)*safari/i.test(
          navigator.userAgent,
        )
        const errorMessage = isSafari
          ? "Permission denied. Please allow camera and microphone access in Safari settings and ensure you're using HTTPS."
          : error.message

        this.notifyListeners('permissionDenied', { error: errorMessage })
      } else if (error.name === 'NotFoundError') {
        this.notifyListeners('deviceNotFound', { error: error.message })
      } else if (error.name === 'NotReadableError') {
        this.notifyListeners('deviceInUse', { error: error.message })
      } else if (error.name === 'SecurityError') {
        // Common Safari error when not on HTTPS
        const isSafari = /^((?!chrome|android).)*safari/i.test(
          navigator.userAgent,
        )
        const errorMessage = isSafari
          ? "Safari requires HTTPS for media access. Please ensure you're using a secure connection."
          : error.message
        this.notifyListeners('securityError', { error: errorMessage })
      }

      throw error
    }
  }

  /**
   * Initializes media source (equivalent to easyrtc.initMediaSource)
   *
   * High-level method that requests media access (if needed) and sets up video/audio
   * elements. This is the main entry point for initializing media for a call.
   * Throws an error if the service is in degraded mode.
   *
   * @param {string} [mediaType='both'] - Type of media to initialize: 'audio', 'video', or 'both'
   * @param {string} conversationId - The conversation ID for this call
   * @returns {Promise<MediaStream>} The initialized media stream
   * @throws {Error} If media functionality is not supported in this browser
   */
  async initMediaSource(mediaType = 'both', conversationId) {
    try {
      // Check if media is supported
      if (this.isDegradedMode()) {
        throw new Error('Media functionality not supported in this browser')
      }

      // Request media access if we don't have a stream
      if (!this.localStream) {
        await this.requestMediaAccess(mediaType)
      }

      // Set up video elements if video is enabled
      if (this.mediaState.videoEnabled) {
        this._setupVideoElements()
      }

      // Set up audio elements if audio is enabled
      if (this.mediaState.audioEnabled) {
        this._setupAudioElements()
      }

      this.notifyListeners('mediaSourceInitialized', {
        mediaType,
        conversationId,
        stream: this.localStream,
      })

      return this.localStream
    } catch (error) {
      console.error('Media source initialization failed:', error)
      this.notifyListeners('mediaSourceInitFailed', { error: error.message })
      throw error
    }
  }

  /**
   * Sets both video element references at once
   *
   * Convenience method to set both self and caller video references simultaneously.
   * These references are used to display local and remote video streams.
   *
   * @param {HTMLVideoElement|null} selfVideoRef - Reference to local video element
   * @param {HTMLVideoElement|null} callerVideoRef - Reference to remote video element
   */
  setVideoRefs(selfVideoRef, callerVideoRef) {
    this.videoRefs.selfVideo = selfVideoRef
    this.videoRefs.callerVideo = callerVideoRef
    /*
    console.log('MediaService - Video refs set:', {
      selfVideo: !!selfVideoRef,
      callerVideo: !!callerVideoRef,
    })
    */
  }

  /**
   * Sets the self video element reference
   *
   * @param {HTMLVideoElement|null} selfVideoRef - Reference to local video element
   */
  setSelfVideoRef(selfVideoRef) {
    this.videoRefs.selfVideo = selfVideoRef
    // console.log('MediaService - Self video ref set:', !!selfVideoRef)
  }

  /**
   * Sets the caller video element reference
   *
   * @param {HTMLVideoElement|null} callerVideoRef - Reference to remote video element
   */
  setCallerVideoRef(callerVideoRef) {
    this.videoRefs.callerVideo = callerVideoRef
    // console.log('MediaService - Caller video ref set:', !!callerVideoRef)
  }

  /**
   * Sets up video elements for display
   *
   * Configures the self video element with the local stream, mutes it to prevent
   * feedback, and attempts to autoplay. This is an internal method called during
   * media initialization.
   *
   * @private
   */
  _setupVideoElements() {
    if (this.videoRefs.selfVideo && this.localStream) {
      // Set video source
      this.videoRefs.selfVideo.srcObject = this.localStream
      this.videoRefs.selfVideo.muted = true // Mute self video to prevent feedback

      // Ensure video plays
      this.videoRefs.selfVideo.play().catch((error) => {
        console.warn('Could not autoplay self video:', error)
      })

      this.notifyListeners('selfVideoReady', {
        videoElement: this.videoRefs.selfVideo,
      })
    }
  }

  /**
   * Sets up audio elements and controls
   *
   * Creates audio visualizer (for audio-only mode) and sets up mute/unmute controls.
   * This is an internal method called during media initialization.
   *
   * @private
   */
  _setupAudioElements() {
    if (this.localStream) {
      // Create audio visualizer if needed
      this._createAudioVisualizer()

      // Set up mute/unmute controls
      this._setupAudioControls()

      this.notifyListeners('audioReady', { stream: this.localStream })
    }
  }

  /**
   * Creates audio visualizer for audio-only mode
   *
   * Creates a canvas-based audio visualizer that displays frequency data from the
   * audio stream. Uses Web Audio API to analyze the audio and render a visual
   * representation. This is an internal method called during audio setup.
   *
   * @private
   */
  _createAudioVisualizer() {
    try {
      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)()
      const analyser = audioContext.createAnalyser()
      const source = audioContext.createMediaStreamSource(this.localStream)

      source.connect(analyser)
      analyser.fftSize = 256

      const bufferLength = analyser.frequencyBinCount
      const dataArray = new Uint8Array(bufferLength)

      // Create canvas for visualization
      const canvas = document.createElement('canvas')
      canvas.className = 'audio-visualizer'
      canvas.width = 200
      canvas.height = 200

      const ctx = canvas.getContext('2d')

      // Animation loop for visualization
      const drawVisualizer = () => {
        if (!this.mediaState.audioEnabled || this.mediaState.audioMuted) {
          return
        }

        requestAnimationFrame(drawVisualizer)

        analyser.getByteFrequencyData(dataArray)

        ctx.fillStyle = 'rgb(0, 0, 0)'
        ctx.fillRect(0, 0, canvas.width, canvas.height)

        const barWidth = (canvas.width / bufferLength) * 2.5
        let barHeight
        let x = 0

        for (let i = 0; i < bufferLength; i++) {
          barHeight = dataArray[i] / 2

          ctx.fillStyle = `rgb(${barHeight + 100},50,50)`
          ctx.fillRect(x, canvas.height - barHeight, barWidth, barHeight)

          x += barWidth + 1
        }
      }

      drawVisualizer()

      this.notifyListeners('audioVisualizerReady', { canvas, audioContext })
    } catch (error) {
      console.warn('Could not create audio visualizer:', error)
    }
  }

  /**
   * Sets up audio controls (mute/unmute)
   *
   * Notifies listeners that audio controls are ready. The actual control UI
   * is implemented by the calling components. This is an internal method.
   *
   * @private
   */
  _setupAudioControls() {
    // This will be called by the UI components
    this.notifyListeners('audioControlsReady', {
      canMute: this.mediaState.audioEnabled,
      isMuted: this.mediaState.audioMuted,
    })
  }

  /**
   * Toggles microphone mute/unmute using EasyRTC's enableMicrophone method
   *
   * Mutes or unmutes the microphone using EasyRTC's enableMicrophone method to
   * maintain compatibility with the legacy system. Sends a data channel message
   * to notify the other participant of the mute state change.
   *
   * @returns {boolean} The new mute state (true = muted, false = unmuted), or false if EasyRTC not available
   */
  toggleAudioMute() {
    if (!this.easyrtc) {
      console.warn('MediaService - EasyRTC instance not available for muting')
      return false
    }

    const newMuteState = !this.mediaState.audioMuted

    // Use EasyRTC's enableMicrophone method (same as legacy implementation)
    this.easyrtc.enableMicrophone(!newMuteState)

    this.mediaState.audioMuted = newMuteState

    this.notifyListeners('audioMuteChanged', {
      muted: newMuteState,
      mediaState: this.mediaState,
    })

    // Send data channel message to other participant about mute state change
    this.notifyListeners('sendDataChannelMessage', {
      messageType: newMuteState ? 'audio_muted' : 'audio_unmuted',
      messageData: {
        action_type: 'mute',
        paused: newMuteState,
      },
    })

    // console.log(
    //   'MediaService - Audio',
    //   newMuteState ? 'muted' : 'unmuted',
    //   'via enableMicrophone',
    // )
    return newMuteState
  }

  /**
   * Toggles video pause/resume using EasyRTC's enableCamera method
   *
   * Pauses or resumes the outbound camera the same way mute uses enableMicrophone
   * (legacy chat.js). Also disables local preview tracks so the coach self-view
   * freezes for clarity (EasyRTC's stream and MediaService.localStream can differ).
   *
   * @returns {boolean} The new pause state (true = paused, false = playing), or false if EasyRTC not available
   */
  toggleVideoPause() {
    if (!this.easyrtc) {
      console.warn(
        'MediaService - EasyRTC instance not available for video pause',
      )
      return false
    }

    const newPauseState = !this.mediaState.videoPaused

    // CTS-9777: stop video to peers (legacy parity with enableCamera)
    this.easyrtc.enableCamera(!newPauseState)

    // CTS-9777: also pause local preview so the coach sees they are paused
    const previewStreams = [this.localStream, this.easyrtc.getLocalStream?.()]
      .filter(Boolean)
      .filter(
        (stream, index, all) =>
          all.findIndex((other) => other === stream) === index,
      )

    previewStreams.forEach((stream) => {
      stream.getVideoTracks().forEach((track) => {
        track.enabled = !newPauseState
      })
    })

    this.mediaState.videoPaused = newPauseState

    this.notifyListeners('videoPauseChanged', {
      paused: newPauseState,
      mediaState: this.mediaState,
    })

    // Send data channel message to other participant about pause state change
    this.notifyListeners('sendDataChannelMessage', {
      messageType: newPauseState ? 'video_paused' : 'video_unpaused',
      messageData: {
        action_type: 'pause',
        paused: newPauseState,
      },
    })

    return newPauseState
  }

  /**
   * Switches to a different audio input device
   *
   * Changes the active audio input device and restarts the stream if one is active.
   * The new device will be used for subsequent media requests.
   *
   * @param {string} deviceId - The device ID of the audio input to switch to
   * @returns {Promise<boolean>} True if the device was switched successfully, false otherwise
   */
  async switchAudioDevice(deviceId) {
    try {
      if (!deviceId || deviceId === this.currentDevices.audioInput) {
        return false
      }

      this.currentDevices.audioInput = deviceId

      // If we have an active stream, restart it with new device
      if (this.localStream) {
        await this.restartStream('audio')
      }

      this.notifyListeners('audioDeviceChanged', { deviceId })
      return true
    } catch (error) {
      console.error('Failed to switch audio device:', error)
      return false
    }
  }

  /**
   * Switches to a different video input device
   *
   * Changes the active video input device and restarts the stream if one is active.
   * The new device will be used for subsequent media requests.
   *
   * @param {string} deviceId - The device ID of the video input to switch to
   * @returns {Promise<boolean>} True if the device was switched successfully, false otherwise
   */
  async switchVideoDevice(deviceId) {
    try {
      if (!deviceId || deviceId === this.currentDevices.videoInput) {
        return false
      }

      this.currentDevices.videoInput = deviceId

      // If we have an active stream, restart it with new device
      if (this.localStream) {
        await this.restartStream('video')
      }

      this.notifyListeners('videoDeviceChanged', { deviceId })
      return true
    } catch (error) {
      console.error('Failed to switch video device:', error)
      return false
    }
  }

  /**
   * Restarts media stream with new device constraints
   *
   * Stops the current stream and requests a new one with updated device constraints.
   * Re-initializes video and audio elements. Used when switching devices.
   *
   * @param {string} [mediaType='both'] - Type of media to restart: 'audio', 'video', or 'both'
   * @returns {Promise<boolean>} True if the stream was restarted successfully, false otherwise
   */
  async restartStream(mediaType = 'both') {
    try {
      if (this.localStream) {
        // Stop all tracks
        this.localStream.getTracks().forEach((track) => track.stop())
        this.localStream = null
      }

      // Request new stream
      await this.requestMediaAccess(mediaType)

      // Re-setup elements
      if (this.mediaState.videoEnabled) {
        this._setupVideoElements()
      }
      if (this.mediaState.audioEnabled) {
        this._setupAudioElements()
      }

      this.notifyListeners('streamRestarted', { mediaType })
      return true
    } catch (error) {
      console.error('Failed to restart stream:', error)
      return false
    }
  }

  /**
   * Handles remote stream received from peer
   *
   * Sets up the remote video element with the received stream and attempts to
   * autoplay. This is called when a remote media stream becomes available
   * (e.g., when the other participant's video/audio stream is received).
   *
   * @param {MediaStream} stream - The remote media stream
   * @param {string} easyrtcid - The EasyRTC ID of the peer sending the stream
   * @returns {boolean} True if the stream was handled successfully, false otherwise
   */
  handleRemoteStream(stream, easyrtcid) {
    try {
      this.remoteStream = stream

      if (this.videoRefs.callerVideo) {
        this.videoRefs.callerVideo.srcObject = stream

        this.videoRefs.callerVideo.play().catch((error) => {
          console.warn('Could not autoplay remote video:', error)
        })

        this.notifyListeners('remoteVideoReady', {
          videoElement: this.videoRefs.callerVideo,
          easyrtcid,
        })
      }

      return true
    } catch (error) {
      console.error('Failed to handle remote stream:', error)
      return false
    }
  }

  /**
   * Gets media statistics
   *
   * Returns detailed statistics about the current media stream including track
   * counts, enabled states, mute/pause states, and video settings (resolution,
   * frame rate) if available.
   *
   * @returns {Object|null} Media statistics object, or null if no local stream
   */
  getMediaStats() {
    if (!this.localStream) return null

    const stats = {
      audioTracks: this.localStream.getAudioTracks().length,
      videoTracks: this.localStream.getVideoTracks().length,
      audioEnabled: this.mediaState.audioEnabled,
      videoEnabled: this.mediaState.videoEnabled,
      audioMuted: this.mediaState.audioMuted,
      videoPaused: this.mediaState.videoPaused,
    }

    // Get track-specific stats if available
    if (this.localStream.getVideoTracks().length > 0) {
      const videoTrack = this.localStream.getVideoTracks()[0]
      if (videoTrack.getSettings) {
        const settings = videoTrack.getSettings()
        stats.videoSettings = {
          width: settings.width,
          height: settings.height,
          frameRate: settings.frameRate,
        }
      }
    }

    return stats
  }

  /**
   * Sets up media event listeners
   *
   * Registers listeners for device changes and permission changes. These listeners
   * automatically update the service state when devices are added/removed or
   * permissions change. This is an internal method called during initialization.
   *
   * @private
   */
  _setupMediaEventListeners() {
    // Listen for device changes (only if supported)
    if (navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
      try {
        navigator.mediaDevices.addEventListener('devicechange', () => {
          this.enumerateDevices()
        })
      } catch (error) {
        console.warn('MediaDevices event listener not supported:', error)
      }
    }

    // Listen for permission changes (only if supported)
    if (navigator.permissions && navigator.permissions.addEventListener) {
      try {
        navigator.permissions.addEventListener('change', () => {
          this.checkPermissions()
        })
      } catch (error) {
        console.warn('Permissions API event listener not supported:', error)
      }
    }
  }

  /**
   * Adds an event listener for media-related events
   *
   * Registers a callback function to be called when a specific event occurs.
   * Supported events include: 'initialized', 'initializationFailed', 'permissionsUpdated',
   * 'devicesUpdated', 'mediaAccessGranted', 'permissionDenied', 'deviceNotFound',
   * 'deviceInUse', 'securityError', 'mediaSourceInitialized', 'mediaSourceInitFailed',
   * 'selfVideoReady', 'audioReady', 'audioVisualizerReady', 'audioControlsReady',
   * 'audioMuteChanged', 'videoPauseChanged', 'audioDeviceChanged', 'videoDeviceChanged',
   * 'streamRestarted', 'remoteVideoReady', 'localStreamSet', 'cleanedUp', and
   * 'sendDataChannelMessage'.
   *
   * @param {string} event - The event name to listen for
   * @param {Function} callback - The callback function to invoke when the event occurs
   */
  addEventListener(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set())
    }
    this.listeners.get(event).add(callback)
  }

  /**
   * Removes an event listener
   *
   * Unregisters a previously registered callback function for a specific event.
   *
   * @param {string} event - The event name
   * @param {Function} callback - The callback function to remove
   */
  removeEventListener(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback)
    }
  }

  /**
   * Notifies all registered listeners of an event
   *
   * Invokes all callback functions registered for the given event with the provided data.
   * Errors in individual callbacks are caught and logged without affecting other listeners.
   *
   * @param {string} event - The event name
   * @param {*} data - Data to pass to the event listeners
   */
  notifyListeners(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach((callback) => {
        try {
          callback(data)
        } catch (error) {
          console.error(`Error in ${event} listener:`, error)
        }
      })
    }
  }

  /**
   * Stops all tracks on a media stream
   *
   * @param {MediaStream|null} stream - The stream to stop
   * @private
   */
  _stopStreamTracks(stream) {
    if (!stream) return

    stream.getTracks().forEach((track) => {
      try {
        track.stop()
      } catch (error) {
        console.warn('MediaService - Error stopping media track:', error)
      }
    })
  }

  /**
   * Releases local camera and microphone without tearing down the whole service.
   *
   * Used when a chat ends. Pause/mute should continue to use track.enabled instead.
   *
   * @param {MediaStream[]} [additionalStreams=[]] - Extra stream references to stop
   */
  releaseLocalMedia(additionalStreams = []) {
    try {
      const streamsToStop = [this.localStream, ...additionalStreams].filter(
        Boolean,
      )
      const stoppedStreamIds = new Set()

      streamsToStop.forEach((stream) => {
        if (stoppedStreamIds.has(stream.id)) return
        stoppedStreamIds.add(stream.id)
        this._stopStreamTracks(stream)
      })

      this.localStream = null

      if (this.videoRefs.selfVideo) {
        this.videoRefs.selfVideo.srcObject = null
      }

      this.mediaState.audioEnabled = false
      this.mediaState.videoEnabled = false
      this.mediaState.audioMuted = false
      this.mediaState.videoPaused = false

      this.notifyListeners('localMediaReleased', {})
    } catch (error) {
      console.error('MediaService - Error releasing local media:', error)
    }
  }

  /**
   * Cleans up and disposes resources
   *
   * Stops all media tracks, clears streams, resets state, and notifies listeners.
   * This should be called when the service is no longer needed or when a call ends.
   */
  cleanup() {
    try {
      this.releaseLocalMedia()

      if (this.remoteStream) {
        this._stopStreamTracks(this.remoteStream)
        this.remoteStream = null
      }

      this.mediaState.permissions = {
        audio: 'unknown',
        video: 'unknown',
      }

      this.notifyListeners('cleanedUp', {})
    } catch (error) {
      console.error('Error during cleanup:', error)
    }
  }

  /**
   * Gets the local media stream
   *
   * @returns {MediaStream|null} The local media stream, or null if not available
   */
  getLocalStream() {
    return this.localStream
  }

  /**
   * Gets the remote media stream
   *
   * @returns {MediaStream|null} The remote media stream, or null if not available
   */
  getRemoteStream() {
    return this.remoteStream
  }

  /**
   * Sets the local stream (used when stream is initialized through EasyRTC)
   *
   * Updates the local stream and media state. This is typically called when a
   * stream is initialized through EasyRTC rather than directly through this service.
   *
   * @param {MediaStream|null} stream - The local media stream to set
   */
  setLocalStream(stream) {
    this.localStream = stream

    if (stream) {
      // Update media state based on the stream
      this.mediaState.audioEnabled = stream.getAudioTracks().length > 0
      this.mediaState.videoEnabled = stream.getVideoTracks().length > 0

      // Notify listeners that we now have a local stream
      this.notifyListeners('localStreamSet', { stream })
    }
  }

  /**
   * Gets a copy of the current media state
   *
   * @returns {Object} A copy of the media state object
   */
  getMediaState() {
    return { ...this.mediaState }
  }

  /**
   * Gets a copy of available media devices
   *
   * @returns {Object} A copy of the media devices object
   */
  getMediaDevices() {
    return { ...this.mediaDevices }
  }

  /**
   * Gets a copy of currently selected devices
   *
   * @returns {Object} A copy of the current devices object
   */
  getCurrentDevices() {
    return { ...this.currentDevices }
  }

  /**
   * Checks if audio is enabled
   *
   * @returns {boolean} True if audio is enabled, false otherwise
   */
  isAudioEnabled() {
    return this.mediaState.audioEnabled
  }

  /**
   * Checks if service is working in degraded mode
   *
   * Degraded mode occurs when media APIs are not supported or when Safari is
   * not in a secure context (HTTPS). In degraded mode, media functionality
   * is unavailable but the service continues to operate.
   *
   * @returns {boolean} True if in degraded mode, false otherwise
   */
  isDegradedMode() {
    const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
    const hasMediaDevices =
      navigator.mediaDevices && navigator.mediaDevices.getUserMedia

    // For Safari, also check if we're in a secure context
    if (isSafari) {
      return !hasMediaDevices || !window.isSecureContext
    }

    return !hasMediaDevices
  }

  /**
   * Checks if service supports media functionality
   *
   * @returns {boolean} True if media is supported, false if in degraded mode
   */
  isMediaSupported() {
    return !this.isDegradedMode()
  }

  /**
   * Gets comprehensive service status information
   *
   * Returns a detailed status object including initialization state, degraded mode
   * status, media support, current media state, stream availability, and device counts.
   *
   * @returns {Object} Service status object with all relevant information
   */
  getServiceStatus() {
    return {
      isInitialized: true,
      isDegradedMode: this.isDegradedMode(),
      isMediaSupported: this.isMediaSupported(),
      mediaState: this.mediaState,
      hasLocalStream: !!this.localStream,
      hasRemoteStream: !!this.remoteStream,
      deviceCount: {
        audioInputs: this.mediaDevices.audioInputs.length,
        videoInputs: this.mediaDevices.videoInputs.length,
        audioOutputs: this.mediaDevices.audioOutputs.length,
      },
    }
  }

  /**
   * Checks if video is enabled
   *
   * @returns {boolean} True if video is enabled, false otherwise
   */
  isVideoEnabled() {
    return this.mediaState.videoEnabled
  }

  /**
   * Checks if audio is muted
   *
   * @returns {boolean} True if audio is muted, false otherwise
   */
  isAudioMuted() {
    return this.mediaState.audioMuted
  }

  /**
   * Checks if video is paused
   *
   * @returns {boolean} True if video is paused, false otherwise
   */
  isVideoPaused() {
    return this.mediaState.videoPaused
  }

  /**
   * Gets processed video stream with background effects
   *
   * Currently returns the original stream. In a full implementation, this would
   * return a processed stream with background blur/removal applied using the
   * Insertable Streams API.
   *
   * @param {string} [backgroundType='none'] - Type of background effect: 'none', 'blur', 'remove', etc.
   * @returns {MediaStream|null} The processed video stream, or null if no local stream
   */
  getProcessedVideoStream(backgroundType = 'none') {
    if (!this.localStream || backgroundType === 'none') {
      return this.localStream
    }

    // For now, return the original stream
    // In a full implementation, this would return a processed stream
    // with background blur/removal applied using Insertable Streams API
    return this.localStream
  }

  /**
   * Checks if background processing is supported
   *
   * Determines if the browser supports the Insertable Streams API required for
   * background blur/removal effects. This requires MediaStreamTrackProcessor and
   * MediaStreamTrackGenerator APIs.
   *
   * @returns {boolean} True if background processing is supported, false otherwise
   */
  isBackgroundProcessingSupported() {
    return (
      typeof MediaStreamTrackProcessor !== 'undefined' &&
      typeof MediaStreamTrackGenerator !== 'undefined'
    )
  }
}

export default MediaService
